import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { api, getAccessToken, setAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useModalScrollLock } from '../lib/modalScrollLock';

interface UserRow {
  id: string;
  tenant_id: string | null;
  email: string;
  full_name: string;
  role: 'super_admin' | 'operator_admin' | 'dispatcher' | 'employee';
  is_active: boolean;
  title: string | null;
  phone: string | null;
  department: string | null;
  profile_image_url: string | null;
  last_login_at: string | null;
  created_at: string;
  // Sprint 42 · super_admin sieht zusätzlich Tenant-Info pro User
  tenant_slug: string | null;
  tenant_name: string | null;
}

interface InviteResult {
  user: { id: string; email: string; full_name: string };
  reset_link: string | null;
  reset_link_expires_at: string | null;
  initial_password: string | null;
}

// Sprint 11 (S11.2) · Operator-Admin Impersonate-Stash-Key (separat von
// Super-Admin-Stash damit beide Origins parallel restorebar sind).
const OPERATOR_ADMIN_STASH_KEY = 'leadcraft_operator_admin_token';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'SUPER-ADMIN',
  operator_admin: 'ADMIN',
  dispatcher: 'NACHBEREITUNG',
  employee: 'ERFASSER',
};

// Sprint 26 · Rollen-Erklärung im Anlegen-Modal (vorher nur dropdown ohne Kontext)
type RoleValue = 'employee' | 'operator_admin' | 'dispatcher';
interface RoleDefinition {
  value: RoleValue;
  label: string;
  description: string;
  bullets: string[];
}
const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    value: 'employee',
    label: 'Erfasser',
    description:
      'Erfasst Leads am Stand mit Mobile-Capture. Sieht nur eigene Leads.',
    bullets: [
      'Lead erfassen (Mobile-PWA)',
      'Visitenkarte fotografieren · OCR · Voice-Diktat',
      'Eigene Leads ansehen + bearbeiten',
      'Eigenes Profil + Profilbild',
    ],
  },
  {
    value: 'dispatcher',
    label: 'Nachbereitung',
    description:
      'Bearbeitet alle Leads des Tenants nach der Messe — Antwort-Mails, Status, Notizen.',
    bullets: [
      'Alle Leads des eigenen Tenants ansehen + bearbeiten',
      'Antwort-Mails versenden (Microsoft Graph)',
      'Status-Pipeline pflegen (NEU → GEPRÜFT → VERSENDET)',
      'Excel-Export aller Leads',
    ],
  },
  {
    value: 'operator_admin',
    label: 'Admin',
    description:
      'Verwaltet Mitarbeiter, Form-Builder, Mail-Templates und Branding für den eigenen Tenant.',
    bullets: [
      'Alles was Nachbereitung darf · plus:',
      'Mitarbeiter anlegen / sperren / Profilbilder',
      'Form-Builder · Mail-Templates · Branding',
      'Microsoft-Graph-Verbindung für Mail-Versand',
    ],
  },
];

/**
 * Sprint 21 (S21.1) · Avatar mit onError-Fallback auf Initialen.
 *
 * Vorher: `<img src={url}>` → bei 401/404 zeigt der Browser ein Broken-Image-
 * Icon (das "Fragezeichen", das Ivan gemeldet hat). Lokaler imgError-State +
 * onError-Handler ersetzt das Bild durch Initialen.
 */
function UserAvatar({ url, fullName }: { url: string | null; fullName: string }) {
  const [errored, setErrored] = useState(false);
  const initials = fullName
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span className="user-name-cell__avatar">
      {url && !errored ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
        />
      ) : (
        <span>{initials || '?'}</span>
      )}
    </span>
  );
}

export function Users() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // Sprint 11 (S11.1) · Persistente Invite-Card statt flüchtigem Toast
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);
  // Sprint 54 · Edit + Delete (Punkt 16 + 17)
  const [editUserTarget, setEditUserTarget] = useState<UserRow | null>(null);

  async function reload() {
    try {
      const list = await api<UserRow[]>('/api/users');
      setRows(list);
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Fehler');
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function toggleActive(u: UserRow) {
    try {
      await api(`/api/users/${u.id}`, {
        method: 'PATCH',
        body: { is_active: !u.is_active },
      });
      await reload();
      setSuccess(`${u.full_name} → ${!u.is_active ? 'aktiviert' : 'deaktiviert'}`);
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Fehler');
    }
  }

  // Sprint 54 · User permanent loeschen (super_admin only · BLOCKED wenn Leads existieren)
  async function deleteUser(u: UserRow) {
    if (!confirm(
      `MITARBEITER LÖSCHEN · ${u.full_name} (${u.email})\n\n`
      + `Permanent löschen? Geht NUR wenn dieser User keine Leads erfasst hat.\n`
      + `Sonst nur 'DEAKTIVIEREN' möglich.`
    )) return;
    try {
      await api(`/api/users/${u.id}?hard=1`, { method: 'DELETE' });
      await reload();
      setSuccess(`${u.full_name} permanent gelöscht`);
      setError(null);
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Fehler';
      // Backend gibt 'user_has_leads:N' zurück → human-readable
      if (detail.startsWith('user_has_leads:')) {
        const n = detail.split(':')[1];
        setError(
          `Löschen blockiert · ${u.full_name} hat ${n} Lead(s) erfasst — `
          + `nutze stattdessen DEAKTIVIEREN.`
        );
      } else {
        setError(detail);
      }
    }
  }

  async function resendInvite(u: UserRow) {
    if (!confirm(`Neuen 7-Tage-Invite-Link generieren + Welcome-Mail an ${u.email} senden?`)) return;
    try {
      const resp = await api<InviteResult>(`/api/users/${u.id}/resend-invite`, {
        method: 'POST',
      });
      // Sprint 11 (S11.1) · Card statt Toast — Admin verliert den Link nicht
      setInviteResult(resp);
      setSuccess(null);
      setError(null);
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Fehler');
    }
  }

  // Sprint 11 (S11.2) · Operator-Admin impersoniert eigenen Tenant-User
  async function impersonateUser(u: UserRow) {
    if (!user) return;
    if (
      !confirm(
        `Als ${u.full_name} (${u.email}) einloggen?\n\nDu erhältst einen 5-Minuten-Token für diesen User. Über den orangen Banner oben kannst du jederzeit zurückwechseln.`,
      )
    ) {
      return;
    }
    try {
      const resp = await api<{
        access_token: string;
        impersonated_user_email: string;
        impersonated_user_full_name: string;
      }>(`/api/users/${u.id}/impersonate`, { method: 'POST' });
      const original = getAccessToken();
      if (original) sessionStorage.setItem(OPERATOR_ADMIN_STASH_KEY, original);
      setAccessToken(resp.access_token);
      await refreshMe();
      navigate('/dashboard');
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Impersonate fehlgeschlagen');
    }
  }

  if (!user) return null;

  const isAdmin = user.role === 'super_admin' || user.role === 'operator_admin';
  const filtered = rows?.filter(
    (u) =>
      !search ||
      u.full_name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <main className="app-shell">
      <AppHeader subtitle="MITARBEITER" />
      <section className="app-shell__main">
        <div className="app-shell__card" style={{ maxWidth: 1100 }}>
          <div className="app-shell__card-label">
            MITARBEITER · {user.branding.tenant_slug?.toUpperCase() ?? 'CROSS-TENANT'} ·{' '}
            {rows?.length ?? '…'}
          </div>
          {/* Sprint 42 · users-toolbar · Header und Button auf einer Zeile,
              auf Mobile umbrechen + Button kleiner. Ersetzt das alte
              lead-detail-section-header das den Button viel zu groß rendert
              und die Liste überlagert. */}
          <div className="users-toolbar">
            <h1 className="app-shell__card-title users-toolbar__title">
              Team-Übersicht
            </h1>
            {isAdmin ? (
              <button
                type="button"
                className="users-toolbar__cta"
                onClick={() => setShowCreate(true)}
              >
                + MITARBEITER ANLEGEN
              </button>
            ) : null}
          </div>

          {success ? <p className="auth-success" role="status">{success}</p> : null}
          {error ? <p className="auth-error" role="alert">{error}</p> : null}

          {inviteResult ? (
            <InviteResultCard
              result={inviteResult}
              onDismiss={() => setInviteResult(null)}
              onResend={async () => {
                if (!inviteResult) return;
                const targetRow = rows?.find((r) => r.id === inviteResult.user.id);
                if (targetRow) await resendInvite(targetRow);
              }}
            />
          ) : null}

          <div className="leads-filters" style={{ marginTop: 16 }}>
            <input
              type="search"
              placeholder="Name oder E-Mail suchen…"
              className="auth-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {rows === null && !error ? <p className="app-shell__card-meta">LÄDT…</p> : null}
          {filtered && filtered.length === 0 ? (
            <p className="app-shell__card-meta" style={{ marginTop: 16 }}>
              Keine Mitarbeiter gefunden.
            </p>
          ) : null}
          {filtered && filtered.length > 0 ? (
            <div className="table-scroll-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>E-MAIL</th>
                  {/* Sprint 42 · super_admin sieht zusätzlich Tenant-Spalte */}
                  {user.role === 'super_admin' ? <th>TENANT</th> : null}
                  <th>NIEDERLASSUNG</th>
                  <th>ROLLE</th>
                  <th>STATUS</th>
                  <th>LETZTER LOGIN</th>
                  {isAdmin ? <th>AKTIONEN</th> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="user-name-cell">
                        <UserAvatar url={u.profile_image_url} fullName={u.full_name} />
                        <span>
                          {u.full_name}
                          {u.title ? <small className="user-name-cell__title"> · {u.title}</small> : null}
                        </span>
                      </div>
                    </td>
                    <td className="admin-table__mono">{u.email}</td>
                    {user.role === 'super_admin' ? (
                      <td>
                        {u.tenant_name ? (
                          <span className="tenant-tag">
                            {u.tenant_slug?.toUpperCase() ?? '—'}
                            <small> · {u.tenant_name}</small>
                          </span>
                        ) : (
                          <span className="admin-table__mono" style={{ opacity: 0.4 }}>
                            — global —
                          </span>
                        )}
                      </td>
                    ) : null}
                    <td className="admin-table__mono">
                      {u.department ?? <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td>
                      <span className={`status-pill role-pill role-pill--${u.role}`}>
                        {ROLE_LABELS[u.role] ?? u.role}
                      </span>
                    </td>
                    <td>
                      {u.is_active ? (
                        // Sprint 54 (Punkt 18) · --tfc-success statt --tfc-green
                        // damit AKTIV bei jedem Tenant-Branding gruen bleibt.
                        <span style={{ color: 'var(--tfc-success)' }}>● AKTIV</span>
                      ) : (
                        <span style={{ color: '#999' }}>○ INAKTIV</span>
                      )}
                    </td>
                    <td className="admin-table__mono">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString('de-DE')
                        : '— nie —'}
                    </td>
                    {isAdmin ? (
                      <td>
                        <div className="user-row-actions">
                          {u.id !== user.id && u.role !== 'super_admin' ? (
                            <>
                              <button
                                type="button"
                                className="app-shell__logout"
                                onClick={() => setEditUserTarget(u)}
                                title="Niederlassung · Telefon · Funktion · Rolle nachträglich ändern"
                              >
                                BEARBEITEN
                              </button>
                              <button
                                type="button"
                                className="app-shell__logout"
                                onClick={() => toggleActive(u)}
                                title={u.is_active ? 'Deaktivieren' : 'Aktivieren'}
                              >
                                {u.is_active ? 'DEAKTIV.' : 'AKTIV.'}
                              </button>
                              <button
                                type="button"
                                className="app-shell__logout"
                                onClick={() => resendInvite(u)}
                                title="Neuer 7-Tage-Invite-Link + Welcome-Mail"
                              >
                                INVITE
                              </button>
                              {u.is_active ? (
                                <button
                                  type="button"
                                  className="app-shell__logout"
                                  onClick={() => impersonateUser(u)}
                                  title="5-Min-Token für diesen User · Banner zum Zurückwechseln"
                                >
                                  ALS USER EINLOGGEN
                                </button>
                              ) : null}
                              {/* Sprint 54 · Hard-Delete · super_admin only · BLOCKED bei Leads */}
                              {user.role === 'super_admin' ? (
                                <button
                                  type="button"
                                  className="app-shell__logout"
                                  onClick={() => deleteUser(u)}
                                  title="Permanent löschen (nur möglich wenn keine Leads)"
                                  style={{ color: 'var(--tfc-state-error)' }}
                                >
                                  LÖSCHEN
                                </button>
                              ) : null}
                            </>
                          ) : null}
                          {u.id === user.id ? (
                            <button
                              type="button"
                              className="app-shell__logout"
                              onClick={() => navigate('/profile')}
                            >
                              MEIN PROFIL
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : null}
        </div>

        {showCreate ? (
          <CreateUserModal
            currentTenantId={user.tenant_id}
            onClose={() => setShowCreate(false)}
            onCreated={async (result) => {
              setShowCreate(false);
              await reload();
              // Sprint 11 (S11.1) · Persistente Card statt flüchtigem Toast
              setInviteResult(result);
              setSuccess(null);
            }}
          />
        ) : null}

        {editUserTarget ? (
          <EditUserModal
            target={editUserTarget}
            actorRole={user.role}
            onClose={() => setEditUserTarget(null)}
            onSaved={async () => {
              setEditUserTarget(null);
              await reload();
              setSuccess('Mitarbeiter aktualisiert');
              setError(null);
            }}
          />
        ) : null}
      </section>
    </main>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  currentTenantId: string | null;
  onClose: () => void;
  onCreated: (result: InviteResult) => void | Promise<void>;
}) {
  // Sprint 44 · Body-Scroll-Lock damit Hintergrund nicht mitscrollt
  useModalScrollLock(true);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'employee' | 'operator_admin' | 'dispatcher'>('employee');
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [department, setDepartment] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const resp = await api<InviteResult>('/api/users', {
        method: 'POST',
        body: {
          email,
          full_name: fullName,
          role,
          title: title || null,
          phone: phone || null,
          department: department || null,
          // Sprint 43 · Custom-Text in Welcome-Mail (zwischen Begrüßung+Button)
          invite_custom_message: customMessage || null,
        },
      });
      await onCreated(resp);
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Anlegen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="app-shell__card-title">Neuen Mitarbeiter anlegen</h2>
        <form onSubmit={submit} className="auth-form">
          <div className="auth-field">
            <label className="auth-field__label">VOLLSTÄNDIGER NAME *</label>
            <input
              className="auth-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              minLength={2}
              maxLength={200}
              required
              autoFocus
            />
          </div>
          <div className="auth-field">
            <label className="auth-field__label">E-MAIL *</label>
            <input
              type="email"
              className="auth-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="auth-field">
            <label className="auth-field__label">ROLLE *</label>
            <div className="role-cards" role="radiogroup" aria-label="Rolle wählen">
              {ROLE_DEFINITIONS.map((r) => (
                <label
                  key={r.value}
                  className={`role-card ${role === r.value ? 'role-card--active' : ''}`}
                >
                  <input
                    type="radio"
                    name="user-role"
                    value={r.value}
                    checked={role === r.value}
                    onChange={() => setRole(r.value)}
                    className="role-card__radio"
                  />
                  <div className="role-card__body">
                    <div className="role-card__head">
                      <span className="role-card__title">{r.label}</span>
                      <span className="role-card__code">{r.value}</span>
                    </div>
                    <p className="role-card__desc">{r.description}</p>
                    <ul className="role-card__bullets">
                      {r.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="auth-field">
            <label className="auth-field__label">TITEL / POSITION</label>
            <input
              className="auth-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Sales Manager"
              maxLength={120}
            />
          </div>
          <div className="auth-field">
            <label className="auth-field__label">TELEFON</label>
            <input
              className="auth-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={40}
            />
          </div>
          {/* Sprint 42 · Niederlassung-Tag */}
          <div className="auth-field">
            <label className="auth-field__label">NIEDERLASSUNG / TOCHTER</label>
            <input
              className="auth-input"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="z.B. TFC Berlin · STRACKE Süd"
              maxLength={120}
            />
          </div>
          {/* Sprint 43 · Persönlicher Text in Welcome-Mail */}
          <div className="auth-field">
            <label className="auth-field__label">
              PERSÖNLICHE NACHRICHT (optional)
            </label>
            <textarea
              className="auth-input form-field__textarea"
              rows={3}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="z.B. Hi Max, freu mich dich im Team zu haben — bei Fragen melde dich!"
              maxLength={2000}
            />
            <p className="form-field__hint">
              Wird in der Einladungs-Mail zwischen Begrüßung und Setup-Button
              eingefügt. Plain-Text · Zeilenumbrüche werden zu &lt;br&gt;.
            </p>
          </div>
          {error ? <p className="auth-error">{error}</p> : null}
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? 'LEGT AN…' : 'ANLEGEN + INVITE'}
            </button>
            <button
              type="button"
              className="app-shell__logout"
              onClick={onClose}
              disabled={busy}
            >
              ABBRECHEN
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Sprint 11 (S11.1) · Persistente Invite-Result-Card.
 *
 * Wird angezeigt nach `POST /api/users` oder `POST /api/users/:id/resend-invite`.
 * Zeigt Reset-Link + Copy-Button + "Mail erneut senden"-Button. Bleibt sichtbar
 * bis der Admin "Verstanden" klickt — Toast war zu flüchtig (Admin verlor Link).
 */
function InviteResultCard({
  result,
  onDismiss,
  onResend,
}: {
  result: InviteResult;
  onDismiss: () => void;
  onResend: () => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState(false);

  async function copyLink() {
    if (!result.reset_link) return;
    try {
      await navigator.clipboard.writeText(result.reset_link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Browser-Fallback: prompt() — User kann manuell kopieren
      window.prompt('Reset-Link kopieren:', result.reset_link);
    }
  }

  async function resend() {
    setResending(true);
    try {
      await onResend();
    } finally {
      setResending(false);
    }
  }

  const expiresAt = result.reset_link_expires_at
    ? new Date(result.reset_link_expires_at)
    : null;

  return (
    <div
      className="app-shell__card"
      style={{
        marginTop: 16,
        background: '#F0FAF4',
        borderColor: '#34C369',
        borderWidth: 2,
        borderStyle: 'solid',
      }}
    >
      <div className="app-shell__card-label" style={{ color: '#0F8C3F' }}>
        INVITE GESENDET · {result.user.email.toUpperCase()}
      </div>
      <h3 className="app-shell__card-title" style={{ marginTop: 4 }}>
        {result.user.full_name} kann jetzt sein Passwort setzen
      </h3>
      <p className="app-shell__card-meta" style={{ marginTop: 8 }}>
        Eine Welcome-Mail mit Set-Password-Link wurde an{' '}
        <strong>{result.user.email}</strong> gesendet
        {expiresAt
          ? ` · Link gültig bis ${expiresAt.toLocaleDateString('de-DE')} (${Math.round(
              (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
            )} Tage).`
          : '.'}
      </p>

      {result.reset_link ? (
        <div style={{ marginTop: 12 }}>
          <label className="auth-field__label">DIREKT-LINK (FALLS MAIL NICHT ANKOMMT)</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <input
              className="auth-input"
              type="text"
              readOnly
              value={result.reset_link}
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="auth-submit"
              style={{ width: 'auto', padding: '0 16px', flexShrink: 0 }}
              onClick={copyLink}
            >
              {copied ? 'KOPIERT' : 'KOPIEREN'}
            </button>
          </div>
        </div>
      ) : null}

      {result.initial_password ? (
        <div style={{ marginTop: 12 }}>
          <label className="auth-field__label">FALLBACK · TEMP-PASSWORT (MAIL DOWN)</label>
          <code
            style={{
              display: 'block',
              padding: 8,
              background: '#fff',
              border: '1px solid #E8E8EA',
              borderRadius: 6,
              fontFamily: 'JetBrains Mono, monospace',
              marginTop: 4,
            }}
          >
            {result.initial_password}
          </code>
          <p className="form-field__hint">
            User soll bevorzugt den Link oben benutzen — dieses temporäre PW ist nur Fallback.
          </p>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="auth-submit"
          style={{ width: 'auto', padding: '10px 20px' }}
          onClick={onDismiss}
        >
          VERSTANDEN
        </button>
        <button
          type="button"
          className="app-shell__logout"
          onClick={resend}
          disabled={resending}
          title="Generiert einen neuen Link + verschickt Mail erneut"
        >
          {resending ? 'SENDE…' : 'MAIL ERNEUT SENDEN'}
        </button>
      </div>
    </div>
  );
}

// Sprint 54 (Punkt 17) · Mitarbeiter nachtraeglich bearbeiten ·
// Backend: PATCH /api/users/:id (UserUpdateAdmin) ·
// Felder: full_name · title · phone · department · role · is_active.
// E-Mail bleibt unveraenderlich (login-key).
function EditUserModal({
  target,
  actorRole,
  onClose,
  onSaved,
}: {
  target: UserRow;
  actorRole: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  useModalScrollLock(true);

  const [fullName, setFullName] = useState(target.full_name);
  const [title, setTitle] = useState(target.title ?? '');
  const [phone, setPhone] = useState(target.phone ?? '');
  const [department, setDepartment] = useState(target.department ?? '');
  const [role, setRole] = useState<RoleValue>(
    (target.role === 'super_admin' ? 'employee' : target.role) as RoleValue,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/api/users/${target.id}`, {
        method: 'PATCH',
        body: {
          full_name: fullName,
          title: title || null,
          phone: phone || null,
          department: department || null,
          role,
        },
      });
      await onSaved();
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="app-shell__card-title">Mitarbeiter bearbeiten</h2>
        <p className="app-shell__card-meta admin-table__mono" style={{ marginBottom: 16 }}>
          {target.email}
        </p>

        {error ? <p className="auth-error" role="alert">{error}</p> : null}

        <form onSubmit={submit} className="auth-form">
          <div className="auth-field">
            <label className="auth-field__label">VOLLSTÄNDIGER NAME *</label>
            <input
              className="auth-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              minLength={2}
              maxLength={200}
              required
            />
          </div>

          <div className="auth-field">
            <label className="auth-field__label">FUNKTION / TITEL</label>
            <input
              className="auth-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="z.B. Außendienst · Geschäftsführer"
            />
          </div>

          <div className="auth-field">
            <label className="auth-field__label">TELEFON</label>
            <input
              className="auth-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={64}
              placeholder="z.B. +49 …"
            />
          </div>

          <div className="auth-field">
            <label className="auth-field__label">NIEDERLASSUNG / TAG</label>
            <input
              className="auth-input"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              maxLength={120}
              placeholder="z.B. TFC Berlin · STRACKE Süd"
            />
          </div>

          {actorRole === 'super_admin' || actorRole === 'operator_admin' ? (
            <div className="auth-field">
              <label className="auth-field__label">ROLLE</label>
              <select
                className="auth-input"
                value={role}
                onChange={(e) => setRole(e.target.value as RoleValue)}
              >
                <option value="employee">Erfasser</option>
                <option value="dispatcher">Nachbereitung</option>
                <option value="operator_admin">Admin</option>
              </select>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? 'SPEICHERT…' : 'SPEICHERN'}
            </button>
            <button
              type="button"
              className="app-shell__logout"
              onClick={onClose}
              disabled={busy}
            >
              ABBRECHEN
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
